import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Tabs, Tab, Box } from '@mui/material';
import EditEventDetails from '../../events/eventManagement/EditEventDetails';
import EditAdditionalDetails from '../../events/eventManagement/EditAdditionalDetails';
import EditEventOrganizerDetails from '../../events/eventManagement/EditEventOrganizerDetails';
import EditEventPolicy from '../../events/eventManagement/EditRefundPolicy';
import api, { apiMultiPart } from '../../../services/api';

const EventEditModal = ({ open, onClose, event, onSaved }) => {
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);

  const handleTabChange = (e, newValue) => setTab(newValue);

  // Handlers to bridge child saves to API calls
  const handleSaveEventDetails = async (formData) => {
    if (!event?.id) return;
    setSaving(true);
    try {
      await apiMultiPart.put(`/v1/api/events/${event.id}`, formData);
      onSaved?.();
    } catch (err) {
      console.error('Failed to save event details', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOrganizer = async (formData) => {
    if (!event?.id) return;
    setSaving(true);
    try {
      await apiMultiPart.put(`/v1/api/events/${event.id}/organizer`, formData);
      onSaved?.();
    } catch (err) {
      console.error('Failed to save organizer details', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveKeyTakeaways = async (keyTakeaways) => {
    if (!event?.id) return;
    // Map to string array if needed
    const payload = (keyTakeaways || []).map(k => k.text?.trim()).filter(Boolean);
    try {
      await api.put(`/v1/api/events/${event.id}/key-takeaways`, payload);
      onSaved?.();
    } catch (err) {
      console.error('Failed to save key takeaways', err);
    }
  };

  const handleSaveSpeakers = async (speaker) => {
    if (!event?.id || !speaker) return;
    try {
      if (speaker.id && speaker.id !== -1) {
        // Update existing speaker
        await api.put(`/v1/api/events/${event.id}/speakers/${speaker.id}`, speaker);
      } else {
        // Create new speaker (uses multipart if image is a File)
        const formData = new FormData();
        formData.append('name', speaker.name);
        formData.append('organization', speaker.organization || '');
        formData.append('organizationRole', speaker.organizationRole || '');
        if (speaker.image && speaker.image instanceof File) {
          formData.append('image', speaker.image);
        }
        await apiMultiPart.post(`/v1/api/events/${event.id}/speakers`, formData);
      }
      onSaved?.();
    } catch (err) {
      console.error('Failed to save speaker', err);
    }
  };

  const handleRemoveSpeaker = async (speakerId) => {
    if (!event?.id || !speakerId || speakerId === -1) return;
    try {
      await api.delete(`/v1/api/events/${event.id}/speakers/${speakerId}`);
      onSaved?.();
    } catch (err) {
      console.error('Failed to remove speaker', err);
    }
  };

  const handleSavePolicies = async (policies) => {
    if (!event?.id) return;
    try {
      await api.put(`/v1/api/events/${event.id}/policies`, policies);
      onSaved?.();
    } catch (err) {
      console.error('Failed to save policies', err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Edit Event</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tab} onChange={handleTabChange} variant="scrollable" scrollButtons allowScrollButtonsMobile>
            <Tab label="Event Details" />
            <Tab label="Additional" />
            <Tab label="Organizer" />
            <Tab label="Policies" />
          </Tabs>
        </Box>

        {tab === 0 && (
          <EditEventDetails event={event} onSave={handleSaveEventDetails} isEditable={false} />
        )}
        {tab === 1 && (
          <EditAdditionalDetails
            event={event}
            onSaveKeyTakeaways={handleSaveKeyTakeaways}
            onSaveSpeakers={handleSaveSpeakers}
            onRemoveSpeaker={handleRemoveSpeaker}
          />
        )}
        {tab === 2 && (
          <EditEventOrganizerDetails organizer={event?.organizer} onSave={handleSaveOrganizer} isEditable={false} />
        )}
        {tab === 3 && (
          <EditEventPolicy initialPolicies={event?.policies} onSave={handleSavePolicies} isEditable={false} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventEditModal;
